import React from "react";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import BrushRoundedIcon from "@mui/icons-material/BrushRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import CloudRoundedIcon from "@mui/icons-material/CloudRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import BugReportRoundedIcon from "@mui/icons-material/BugReportRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import ScienceRoundedIcon from "@mui/icons-material/ScienceRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import SettingsSuggestRoundedIcon from "@mui/icons-material/SettingsSuggestRounded";

/**
 * The one place a project's mark is defined.
 *
 * A project stores only two ids — `avatar.icon` and `avatar.color` — and every
 * screen draws the mark from this catalogue, so the same project looks the same
 * in the create dialog, the projects table and the sidebar. Nothing is fetched:
 * the glyphs are MUI icons that already ship with the app, so the mark renders
 * offline and never waits on a network round trip.
 *
 * Ids must stay in step with server/src/services/projectAvatars.js, which is
 * what refuses anything outside this list.
 */

export const PROJECT_AVATAR_ICONS = [
  { id: "rocket", label: "Launch", Icon: RocketLaunchRoundedIcon },
  { id: "code", label: "Engineering", Icon: CodeRoundedIcon },
  { id: "mobile", label: "Mobile app", Icon: PhoneIphoneRoundedIcon },
  { id: "web", label: "Website", Icon: LanguageRoundedIcon },
  { id: "design", label: "Design", Icon: BrushRoundedIcon },
  { id: "database", label: "Data platform", Icon: StorageRoundedIcon },
  { id: "cloud", label: "Cloud & infra", Icon: CloudRoundedIcon },
  { id: "analytics", label: "Analytics", Icon: InsightsRoundedIcon },
  { id: "security", label: "Security", Icon: ShieldRoundedIcon },
  { id: "quality", label: "Quality & QA", Icon: BugReportRoundedIcon },
  { id: "support", label: "Support desk", Icon: SupportAgentRoundedIcon },
  { id: "marketing", label: "Marketing", Icon: CampaignRoundedIcon },
  { id: "commerce", label: "E-commerce", Icon: ShoppingCartRoundedIcon },
  { id: "finance", label: "Finance", Icon: AccountBalanceRoundedIcon },
  { id: "people", label: "People & HR", Icon: GroupsRoundedIcon },
  { id: "research", label: "Research", Icon: ScienceRoundedIcon },
  { id: "ai", label: "AI & ML", Icon: AutoAwesomeRoundedIcon },
  { id: "operations", label: "Operations", Icon: SettingsSuggestRoundedIcon },
];

export const PROJECT_AVATAR_COLORS = [
  { id: "violet", label: "Violet", from: "#7C5CFF", to: "#4B2FD6" },
  { id: "blue", label: "Blue", from: "#3B9BFF", to: "#1657C8" },
  { id: "teal", label: "Teal", from: "#2ED3C6", to: "#0E8F87" },
  { id: "green", label: "Green", from: "#45CC7A", to: "#0E8A47" },
  { id: "amber", label: "Amber", from: "#FFB83B", to: "#D07B06" },
  { id: "orange", label: "Orange", from: "#FF8A4C", to: "#D64B12" },
  { id: "rose", label: "Rose", from: "#FF6B8B", to: "#D2254E" },
  { id: "slate", label: "Slate", from: "#8792A8", to: "#4A5568" },
];

export const DEFAULT_PROJECT_AVATAR = {
  icon: PROJECT_AVATAR_ICONS[0].id,
  color: PROJECT_AVATAR_COLORS[0].id,
};

export const findAvatarIcon = (iconId) =>
  PROJECT_AVATAR_ICONS.find((option) => option.id === iconId) || null;

export const findAvatarColor = (colorId) =>
  PROJECT_AVATAR_COLORS.find((option) => option.id === colorId) || null;

export const gradientOf = (color) =>
  `linear-gradient(135deg, ${color.from} 0%, ${color.to} 100%)`;

/* a stable number from the project's name, so the same project always lands on
   the same colour without anything being stored for it */
const hashOf = (text = "") => {
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) % 100000;
  return hash;
};

/* The fill for a project that never picked an avatar: one flat colour from the
   same palette the picker offers — a single letter on a solid tile, not a
   gradient, so an unpicked project reads as plainly "not set up yet" next to
   the projects that did choose a mark. */
export const plainColorForProject = (title = "") =>
  PROJECT_AVATAR_COLORS[hashOf(title) % PROJECT_AVATAR_COLORS.length].from;

/**
 * Work out what to draw for a project.
 *
 * @param {{icon?: string, color?: string}} avatar the stored id pair
 * @param {string} title used for the fallback initial and colour
 * @returns {{Icon: React.ComponentType|null, background: string, initial: string}}
 */
export const resolveProjectAvatar = (avatar, title = "") => {
  const icon = findAvatarIcon(avatar?.icon);
  const color = findAvatarColor(avatar?.color) || PROJECT_AVATAR_COLORS[0];

  if (!icon) {
    /* never picked one — keep the initial-letter mark rather than inventing a
       glyph the team did not choose */
    return {
      Icon: null,
      background: plainColorForProject(title),
      initial: (String(title) || "?").charAt(0).toUpperCase(),
    };
  }

  return {
    Icon: icon.Icon,
    background: gradientOf(color),
    initial: (String(title) || "?").charAt(0).toUpperCase(),
  };
};

/**
 * The project's mark. `className` is passed straight through so each screen
 * keeps its own size and radius — this only decides the fill and what is drawn
 * inside it.
 */
export const ProjectAvatar = ({
  avatar,
  title = "",
  size,
  className = "",
  style,
}) => {
  const { Icon, background, initial } = resolveProjectAvatar(avatar, title);
  const box = size ? { width: size, height: size } : null;

  return (
    <span
      className={`pj-project-avatar ${className}`.trim()}
      style={{ background, ...box, ...style }}
      aria-hidden="true"
    >
      {Icon ? <Icon className="pj-project-avatar__icon" /> : initial}
    </span>
  );
};

export default ProjectAvatar;
